import type { ReactNode } from "react"
import { useRef, useState } from "react"

import { useMsal } from "@azure/msal-react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { MemoryItem } from "@/api/memory"

import { getToken } from "@/api/auth-api"
import {
  addMemoryApi,
  approveMemoryCandidateApi,
  deleteMemoryApi,
  getMemoriesApi,
  triggerMaintenanceApi,
  updateMemoryApi,
} from "@/api/memory"
import { Check, Edit, Plus, Save, Trash2, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { MAX_ADD_TO_MEMORY_TEXT_LENGTH } from "@/features/chat/components/system-message-utils"
import { PrismConversationsSection } from "@/features/personalization/components/prism-conversations-section"
import { usePrismMemory } from "@/hooks/use-prism-memory"

interface MemoryPanelProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children?: ReactNode
}

export function MemoryPanel({ open, onOpenChange, children }: MemoryPanelProps) {
  const prismMemory = usePrismMemory()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { instance } = useMsal()

  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = open !== undefined
  const isOpen = isControlled ? open : internalOpen
  const setIsOpen = isControlled ? onOpenChange! : setInternalOpen

  const [activeTab, setActiveTab] = useState<"stm" | "ltm">("stm")
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState("")
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [newMemoryText, setNewMemoryText] = useState("")
  const [newMemoryType, setNewMemoryType] = useState<"stm" | "ltm">("stm")

  const { data: memories, isLoading } = useQuery({
    queryKey: ["memories"],
    queryFn: async () => {
      const tokenResult = await getToken(instance)
      return getMemoriesApi(tokenResult?.accessToken)
    },
    enabled: !!isOpen && !prismMemory,
  })

  // Drives the "new memories to review" tab indicator off the same status
  // field the accept action already uses — no new state to track.
  const hasUnreviewedMemories = (memories?.stm ?? []).some((item) => item.status === "suggested")

  // Set synchronously on the first click so a double click can't start a
  // second save before `isPending` re-renders the button disabled (#3322 B6).
  const addInFlightRef = useRef(false)

  const addMutation = useMutation({
    mutationFn: async (variables: { type: "stm" | "ltm"; text: string }) => {
      const tokenResult = await getToken(instance)
      return addMemoryApi(variables.type, { text: variables.text }, tokenResult?.accessToken)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["memories"] })
      setIsAddDialogOpen(false)
      setNewMemoryText("")
      toast.success("Memory added")
    },
    onError: () => {
      toast.error("Failed to save memory")
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (variables: { id: string; type: "stm" | "ltm"; text: string }) => {
      const tokenResult = await getToken(instance)
      return updateMemoryApi({
        memoryId: variables.id,
        memoryType: variables.type,
        updates: { text: variables.text },
        idToken: tokenResult?.accessToken,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["memories"] })
      setEditingId(null)
      setEditText("")
      toast.success("Memory updated")
    },
    onError: () => {
      toast.error("Failed to save memory")
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (variables: { id: string; type: "stm" | "ltm" }) => {
      const tokenResult = await getToken(instance)
      return deleteMemoryApi(variables.id, variables.type, tokenResult?.accessToken)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["memories"] })
      toast.success("Memory deleted")
    },
    onError: () => {
      toast.error("Failed to delete memory")
    },
  })

  const approveMutation = useMutation({
    mutationFn: async (variables: { text: string }) => {
      const tokenResult = await getToken(instance)
      return approveMemoryCandidateApi(variables.text, tokenResult?.accessToken)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["memories"] })
      toast.success("Memory saved")
    },
    onError: () => {
      toast.error("Failed to save memory")
    },
  })

  const maintenanceMutation = useMutation({
    mutationFn: async () => {
      const tokenResult = await getToken(instance)
      return triggerMaintenanceApi(tokenResult?.accessToken)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["memories"] })
      toast.success("Maintenance job triggered")
    },
  })

  const handleEditStart = (item: MemoryItem) => {
    setEditingId(item.id)
    setEditText(item.text)
  }

  const handleEditSave = (item: MemoryItem, type: "stm" | "ltm") => {
    updateMutation.mutate({ id: item.id, type, text: editText })
  }

  const handleAdd = () => {
    if (addInFlightRef.current) return
    addInFlightRef.current = true
    addMutation.mutate(
      { type: newMemoryType, text: newMemoryText },
      {
        onSettled: () => {
          addInFlightRef.current = false
        },
      }
    )
  }

  if (prismMemory) {
    return (
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        {children && <SheetTrigger asChild>{children}</SheetTrigger>}
        <SheetContent className="flex w-[400px] flex-col sm:w-[540px]">
          <SheetHeader>
            <SheetTitle>{t("personalization.memoryPanel.prism.title")}</SheetTitle>
            <SheetDescription>
              {t("personalization.memoryPanel.prism.description")}
            </SheetDescription>
          </SheetHeader>
          <PrismConversationsSection active={!!isOpen} />
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      {children && <SheetTrigger asChild>{children}</SheetTrigger>}
      <SheetContent className="flex w-[400px] flex-col sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>My Memory</SheetTitle>
          <SheetDescription>View and manage what the AI remembers about you.</SheetDescription>
        </SheetHeader>

        <div className="my-4 flex items-center justify-between">
          <Button size="sm" onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Memory
          </Button>
          <Button
            disabled={maintenanceMutation.isPending}
            size="sm"
            variant="ghost"
            onClick={() => maintenanceMutation.mutate()}
          >
            {maintenanceMutation.isPending ? "Running..." : "Run Maintenance"}
          </Button>
        </div>

        <Tabs
          className="flex flex-1 flex-col overflow-hidden"
          value={activeTab}
          onValueChange={(v: string) => setActiveTab(v as "stm" | "ltm")}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="stm">
              Short-Term (STM)
              {hasUnreviewedMemories && (
                <span aria-hidden="true" className="bg-primary inline-block size-2 rounded-full" />
              )}
              {hasUnreviewedMemories && (
                <span className="sr-only">{t("pages.memory.unreviewed")}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="ltm">Long-Term (LTM)</TabsTrigger>
          </TabsList>

          <TabsContent className="flex-1 overflow-hidden" value="stm">
            <ScrollArea className="h-full pr-4">
              {isLoading ? (
                <div>Loading...</div>
              ) : (
                <div className="mt-4 space-y-4">
                  {memories?.stm.map((item) => (
                    <Card key={item.id}>
                      <CardContent className="p-4">
                        {editingId === item.id ? (
                          <div className="space-y-2">
                            <Textarea
                              maxLength={MAX_ADD_TO_MEMORY_TEXT_LENGTH}
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                            />
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                                <X className="h-4 w-4" />
                              </Button>
                              <Button size="sm" onClick={() => handleEditSave(item, "stm")}>
                                <Save className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-2">
                            <p className="min-w-0 text-sm break-all">{item.text}</p>
                            <div className="flex shrink-0 gap-1">
                              {item.status === "suggested" && (
                                <Button
                                  aria-label="Accept this suggested memory"
                                  className="h-6 w-6"
                                  disabled={approveMutation.isPending}
                                  size="icon"
                                  title="Accept this suggested memory"
                                  variant="ghost"
                                  onClick={() => approveMutation.mutate({ text: item.text })}
                                >
                                  <Check className="text-success h-3 w-3" />
                                </Button>
                              )}
                              <Button
                                className="h-6 w-6"
                                size="icon"
                                variant="ghost"
                                onClick={() => handleEditStart(item)}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button
                                className="text-destructive h-6 w-6"
                                size="icon"
                                variant="ghost"
                                onClick={() => deleteMutation.mutate({ id: item.id, type: "stm" })}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                        <div className="text-muted-foreground mt-2 flex gap-2 text-xs">
                          <span>{new Date(item.created_at).toLocaleDateString()}</span>
                          {item.status && (
                            <Badge className="h-4 text-[10px]" variant="secondary">
                              {item.status}
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {memories?.stm.length === 0 && (
                    <div className="text-muted-foreground py-8 text-center">
                      No short-term memories found.
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          <TabsContent className="flex-1 overflow-hidden" value="ltm">
            <ScrollArea className="h-full pr-4">
              {isLoading ? (
                <div>Loading...</div>
              ) : (
                <div className="mt-4 space-y-4">
                  {memories?.ltm.map((item) => (
                    <Card key={item.id}>
                      <CardContent className="p-4">
                        {editingId === item.id ? (
                          <div className="space-y-2">
                            <Textarea
                              maxLength={MAX_ADD_TO_MEMORY_TEXT_LENGTH}
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                            />
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                                <X className="h-4 w-4" />
                              </Button>
                              <Button size="sm" onClick={() => handleEditSave(item, "ltm")}>
                                <Save className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-2">
                            <p className="min-w-0 text-sm break-all">{item.text}</p>
                            <div className="flex shrink-0 gap-1">
                              <Button
                                className="h-6 w-6"
                                size="icon"
                                variant="ghost"
                                onClick={() => handleEditStart(item)}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button
                                className="text-destructive h-6 w-6"
                                size="icon"
                                variant="ghost"
                                onClick={() => deleteMutation.mutate({ id: item.id, type: "ltm" })}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                        <div className="text-muted-foreground mt-2 flex gap-2 text-xs">
                          {item.type && (
                            <Badge className="h-4 text-[10px]" variant="outline">
                              {item.type}
                            </Badge>
                          )}
                          {item.last_updated_at && (
                            <span>
                              Updated: {new Date(item.last_updated_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {memories?.ltm.length === 0 && (
                    <div className="text-muted-foreground py-8 text-center">
                      No long-term memories found.
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </SheetContent>

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Memory</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Type</legend>
              <div className="flex gap-4">
                <Button
                  variant={newMemoryType === "stm" ? "default" : "outline"}
                  onClick={() => setNewMemoryType("stm")}
                >
                  Short-Term
                </Button>
                <Button
                  variant={newMemoryType === "ltm" ? "default" : "outline"}
                  onClick={() => setNewMemoryType("ltm")}
                >
                  Long-Term
                </Button>
              </div>
            </fieldset>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="new-memory-content">
                Content
              </label>
              <Textarea
                id="new-memory-content"
                maxLength={MAX_ADD_TO_MEMORY_TEXT_LENGTH}
                placeholder="Enter memory content..."
                value={newMemoryText}
                onChange={(e) => setNewMemoryText(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button disabled={!newMemoryText.trim() || addMutation.isPending} onClick={handleAdd}>
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  )
}
